import { getMeetingActorFromSession, isSpectator, MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { isExpert } from "./roles";
import { MeetingOutputDTO, ROLE } from "@kalyzee/kast-web-sdk/http";
import {
  Meeting,
  MeetingActor,
  MeetingSession,
  MeetingSessionRole
} from "@kalyzee/kast-web-sdk/websocket";

export const getFullName = (firstname?: string, lastname?: string): string => {
  return `${firstname ?? "-"} ${lastname ?? "-"}`;
};

export const getExpertActor = (meeting: Meeting): MeetingActor | undefined => {
  return meeting.actors?.find((a) => a.externalProvider === "escaet:expert");
};

export const getActor = (meeting: Meeting, userId: string): MeetingActor | undefined => {
  return meeting.actors?.find((a) => a.user?.id === userId);
};

const getActorUserId = (actor: MeetingOutputDTO["actors"][number]): string | undefined => {
  // _user is the raw reference (always a string ID)
  if (actor._user) return actor._user;
  // user can be populated (object) or a raw string ID
  const u = actor.user;
  if (!u) return undefined;
  if (typeof u === "string") return u;
  return u._id ?? u.id;
};

export const getDefaultMeetingSessionRole = (
  meeting: MeetingOutputDTO,
  userId: string,
  userRole?: ROLE,
): MeetingSessionRole => {
  const actor = meeting.actors?.find(a => getActorUserId(a) === userId);
  const roleMap = meeting.defaultRoleOfNewSessionByUserRole as Record<string, MeetingSessionRole> | undefined;
  return (
    actor?.role ??
    (userRole && roleMap ? roleMap[userRole] : undefined) ??
    roleMap?.defaultValue ??
    MeetingSessionRole.NORMAL
  );
};

export interface ExpectedRoleInfo {
  spectator: boolean;
  expert: boolean;
}

export const getExpectedRoleInfo = (
  meeting: MeetingOutputDTO,
  userId: string,
  userRole?: ROLE,
): ExpectedRoleInfo => {
  const role = getDefaultMeetingSessionRole(meeting, userId, userRole);
  return {
    spectator: isSpectator(role),
    expert: isExpert(role),
  };
};

export const getFormattedUserFromMeetingSession = (
  session: MeetingSession,
  meeting: Meeting
): { avatar?: string; firstname?: string; lastname?: string; fullname: string } => {
  const actor = getMeetingActorFromSession(meeting, session);
  let firstname: string | undefined;
  let lastname: string | undefined;
  let avatar: string | undefined;
  if (actor?.externalProvider === "escaet:room") {
    // user is room
    const expert = getExpertActor(meeting); // get first expert
    if (expert?.user?.firstname || expert?.user?.lastname) {
      avatar = expert.user?.avatar;
      firstname = expert?.user?.firstname;
      lastname = expert?.user?.lastname;
    } else {
      // expert have NOT firstname and lastname
      avatar = session.user?.avatar;
      firstname = session.user?.firstname;
      lastname = session.user?.lastname;
    }
  } else {
    avatar = session.user?.avatar;
    firstname = session.user?.firstname;
    lastname = session.user?.lastname;
  }
  const fullname = getFullName(firstname, lastname);
  return { avatar, firstname, lastname, fullname };
};

export const isExpertVideoSession = (meeting: Meeting | undefined, session: MeetingSessionOvenMediaReceiver): boolean => {
  if (!meeting) return false;
  const allowProviders = ["escaet:expert", "escaet:room"];
  const expertIds = meeting.actors?.filter((a) => a.externalProvider && allowProviders.includes(a.externalProvider) && a.user).map((a) => a.user!.id) ?? [];
  const userId = session.data.session.user?.id;
  return userId ? expertIds.includes(userId) : false;
};

// // Meeting Activity Room

// // Highlighted sessions

// export const respondToSessionHighlightRequest = async (sessionId: string, accept: boolean): Promise<MeetingSession | undefined> => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.respondToTheRequestForHighlighting({ sessionId, accepted: accept });
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.session;
// };

// export const stopSessionHighlight = async (sessionId: string) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.stopHighlighting({ sessionId });
//   const { error, response } = result;
//   if (!response || error) {
//     toastError("Une erreur est apparue.");
//   }
// };

// // Highlighted rooms

// export const startRoomHighlight = async (roomId: string) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.startRoomHighlight({
//     roomId,
//   });
//   const { error, response } = result;
//   if (!response || error) {
//     toastError("Une erreur est apparue.");
//   }
// };

// export const requestRoomHighlight = async (roomId: string) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.requestRoomHighlight({
//     roomId,
//   });
//   const { error, response } = result;
//   if (!response || error) {
//     toastError("Une erreur est apparue.");
//     return false;
//   }
//   return true;
// };

// export const responseRoomHighlight = async (roomId: string, accepted: boolean) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.responseRoomHighlight({
//     roomId,
//     accepted
//   });
//   const { error, response } = result;
//   if (!response || error) {
//     toastError("Une erreur est apparue.");
//     return false;
//   }
//   return true;
// };

// export const stopRoomHighlight = async (roomId: string) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.stopRoomHighlight({
//     roomId,
//   });
//   const { error, response } = result;
//   if (!response || error) {
//     toastError("Une erreur est apparue.");
//     return false;
//   }
//   return true;
// };

// // Moderator call

// export const callModeratorFromRoom = async (roomId: string): Promise<MeetingRoom | undefined> => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.callModeratorFromRoom({ roomId });
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.room;
// };

// export const cancelModeratorCallFromRoom = async (roomId: string): Promise<MeetingRoom | undefined> => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.cancelModeratorCall({ roomId });
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return undefined;
//   }
//   return response.room;
// };

// export const answerRoomModeratorCall = async (roomId: string, accepted: boolean): Promise<MeetingRoom | undefined> => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.answerRoomModeratorCall({ roomId, accepted });
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.room;
// };

// // Rooms

// export const openRoomWithUsers = async (
//   meetingId: string,
//   userIds: string[],
//   mainUserIds?: string[],
//   extra?: string,
// ) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.createRoom({
//     meetingId,
//     type: MeetingRoomType.Group,
//     status: MeetingRoomStatus.Ongoing,
//     enlistedUserIds: userIds,
//     audienceUserIds: userIds,
//     mainUserIds,
//     chatroom: true,
//     reuseIfPossible: true,
//     autoClose: true, // backend closes room when no enlisted user are online
//     uniqueEnlistment: true, // users can't be enlisted in an other room that is not closed
//     extra,
//   } as any /* TODO !*/);
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.room;
// };

// export const updateRoom = async (update: MeetingRoomUpdateRequest) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.updateRoom(update);
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.room;
// };

// // Pairings

// export const createPairings = async (
//   pairingUsers: { id: string; fromMeetingId: string }[],
//   toMeetingId: string,
//   toMeetingRoomId?: string,
// ) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const groupedByMeeting = pairingUsers.reduce((acc, { fromMeetingId: meetingId, id }) => {
//     if (!acc[meetingId]) {
//       acc[meetingId] = [];
//     }
//     acc[meetingId].push(id);
//     return acc;
//   }, {} as Record<string, string[]>);
//   Object.keys(groupedByMeeting).forEach(async (fromMeetingId) => {
//     const userIds = groupedByMeeting[fromMeetingId];
//     await websocket.emitter.meeting.createPairings({
//       id: fromMeetingId,
//       pairings: userIds.map((userId: string) => ({
//         userId,
//         pairedMeetingId: toMeetingId,
//         pairedMeetingRoomId: toMeetingRoomId,
//       })),
//     });
//   });
// };

// export const clearPairings = async (unpairingUsers: { id: string; meetingId: string }[]) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const groupedByMeeting = unpairingUsers.reduce((acc, { meetingId, id }) => {
//     if (!acc[meetingId]) {
//       acc[meetingId] = [];
//     }
//     acc[meetingId].push(id);
//     return acc;
//   }, {} as Record<string, string[]>);
//   Object.keys(groupedByMeeting).forEach(async (fromMeetingId) => {
//     const userIds = groupedByMeeting[fromMeetingId];
//     await websocket.emitter.meeting.clearPairings({
//       id: fromMeetingId,
//       userIds,
//     });
//   });
// };

// // User data

// export const updateSosMode = async (
//   meetingId: MeetingUserDataUpdateRequest["meetingId"],
//   value: boolean,
// ) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.sosMode({
//     id: meetingId,
//     value,
//   });
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.meeting;
// };

// export const updateUserData = async (
//   meetingId: MeetingUserDataUpdateRequest["meetingId"],
//   selection: MeetingUserDataUpdateRequest["selection"],
//   userData: MeetingUserDataUpdateRequest["userData"]
// ) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.updateUserData({
//     meetingId,
//     selection,
//     userData,
//   });
//   const { error, response } = result;
//   if (error || !response) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response.userData;
// };

// export const updateSessionAdminStats = async (
//   meetingId: string,
//   sessionId: string,
//   entries: MeetingSessionUpdateAlertsRequest["entries"]
// ) => {
//   const websocket = sdkClient.getWebsocketClient();
//   const result = await websocket.emitter.meeting.updateAlertsSession({
//     id: meetingId,
//     sessionId,
//     entries,
//   });
//   const { error, response } = result;
//   if (error) {
//     toastError("Une erreur est apparue.");
//     return;
//   }
//   return response;
// }

// export const joinMeeting = async (meetingsAPI: MeetingsAPI, id: string, request?: Omit<MeetingJoinRequest, "id">): Promise<MeetingContainer | undefined> => {
//   const container = meetingsAPI.getOrCreateContainer(id);
//   if (!container) return undefined; // can't join
//   if (container.getStatus() !== "disconnected") return container; // already joined
//   const response = await container.getAPI().join(request);
//   let success = true;
//   if (!response.error) {
//     const meeting = response.response?.meeting;
//     if (meeting) {
//       const session = response.response?.session;
//       if (!session) {
//         success = false;
//         console.error(`[ERROR] join - internal error: session is undefined`);
//       }
//     } else {
//       success = false;
//       console.error(`[ERROR] join - internal error: meeting is undefined`);
//     }
//   } else {
//     success = false;
//     console.error(`[ERROR] join: `, response.error);
//   }

//   if (!success) container.destroy();
//   return success ? container : undefined;
// };
